import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from '@store/hooks';
import { selectSiteId } from '@store/selectors/site.selectors';
import {
  createSchedule,
  fetchSchedules,
  importScheduleFromFile,
  scheduleRetry,
  type ScheduleImportFormat,
} from '@apis/schedule';
import { scheduleKey, scheduleListKey } from '@constants/queryKeys';
import { useActiveScheduleNumber, useSetActiveSchedule } from '@hooks/useActiveSchedule';
import { useToast } from '@components/ui/Toasts/toastContext';
import { ScheduleMenu, type IScheduleMenuProps } from '@components/ScheduleMenu/ScheduleMenu';
import { CreateScheduleModal } from '@components/ScheduleMenu/CreateScheduleModal';
import { importFileFailureToast } from '@components/ScheduleMenu/importFileFailure';
import { useCanAccess } from '@permissions/useFeatures';
import { ChevronRightIcon } from '@utils/icons';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

/**
 * The schedule selector lives in the TopBar breadcrumb so it's reachable from
 * every page, and switching it drives every schedule-aware surface at once (the
 * Dashboard cards, the Activities list) via the global active-schedule store.
 *
 * Two render paths feed the one breadcrumb slot:
 *  - On the Scheduler page, that page `PublishTopBarSchedulePicker`s its own
 *    picker — it owns the URL-driven selection, the unsaved-edits guard and the
 *    bootstrap-aware import, so switching there navigates the Gantt.
 *  - Everywhere else, the self-contained `FallbackSchedulePicker` below drives
 *    off the global working site and just sets the active schedule in place (no
 *    navigation) so the current page's panels update.
 */
type Picker = IScheduleMenuProps | null;

const PickerValueContext = createContext<Picker>(null);
const PickerSetContext = createContext<(picker: Picker) => void>(() => {});

export function TopBarSchedulePickerProvider({ children }: { children: ReactNode }) {
  const [picker, setPicker] = useState<Picker>(null);
  return (
    <PickerSetContext.Provider value={setPicker}>
      <PickerValueContext.Provider value={picker}>{children}</PickerValueContext.Provider>
    </PickerSetContext.Provider>
  );
}

/**
 * Mount inside a page (within its normal, picker-bearing render) to publish a
 * richer picker to the TopBar, overriding the fallback. Unmounting — navigation,
 * an early-return state, or leaving the page — restores the fallback. `picker`
 * must be referentially stable (memoized).
 */
export function PublishTopBarSchedulePicker({ picker }: { picker: IScheduleMenuProps }) {
  const setPicker = useContext(PickerSetContext);
  useEffect(() => {
    setPicker(picker);
    return () => setPicker(null);
  }, [setPicker, picker]);
  return null;
}

/** Rendered by the TopBar breadcrumb. Shows the published picker if a page
 *  provides one, else the global fallback picker. */
export function TopBarSchedulePickerSlot() {
  const published = useContext(PickerValueContext);
  if (published) {
    return (
      <>
        <ScheduleMenu {...published} triggerVariant="breadcrumb" />
        <ChevronRightIcon size={13} />
      </>
    );
  }
  return <FallbackSchedulePicker />;
}

/**
 * The default schedule picker for any page that doesn't publish its own. Driven
 * by the global working site; selecting sets the active schedule in place (the
 * current page's panels react) without leaving the page. Importing a file seeds a
 * new schedule and opens it in the Scheduler. Renders nothing until a site with
 * schedules is known.
 */
function FallbackSchedulePicker() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const siteId = useAppSelector(selectSiteId) ?? '';
  const setActiveSchedule = useSetActiveSchedule();
  const push = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  // Importing and creating are schedule edits; the server refuses them too, and
  // the menu leaves both out for a user without `schedule.edit`.
  const canEdit = useCanAccess('schedule.edit');

  const schedulesQuery = useQuery({
    queryKey: scheduleListKey(siteId),
    queryFn: ({ signal }) => fetchSchedules(siteId, signal),
    enabled: siteId !== '',
    staleTime: 5 * 60_000,
    retry: scheduleRetry,
  });
  const schedules = schedulesQuery.data ?? [];
  const selectedScheduleNumber = useActiveScheduleNumber(siteId, schedules);

  const importMutation = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: ({ file, format }: { file: File; format: ScheduleImportFormat }) =>
      importScheduleFromFile(siteId, file, { format }),
    onSuccess: ({ scheduleNumber, scheduleName }) => {
      setActiveSchedule(siteId, scheduleNumber);
      void qc.invalidateQueries({ queryKey: scheduleListKey(siteId) });
      push({ kind: 'success', title: t('scheduler.import.fileImported', { name: scheduleName }) });
      navigate(`/scheduler/${encodeURIComponent(siteId)}/${encodeURIComponent(scheduleNumber)}`);
    },
    onError: (error) => push(importFileFailureToast(t, error)),
  });

  // Create a new empty schedule, then open it in the Scheduler (mirrors the
  // import mutation's success path). This page has no unsaved-edits guard — it
  // never bootstraps a working copy — so a straight navigate is safe here.
  const createMutation = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (name: string) => createSchedule(siteId, name),
    onSuccess: ({ schedule, scheduleNumber, scheduleName }) => {
      setCreateOpen(false);
      // Seed the detail cache so the Scheduler page opens the new schedule
      // without waiting on (or racing) a refetch.
      qc.setQueryData(scheduleKey(siteId, scheduleNumber), schedule);
      setActiveSchedule(siteId, scheduleNumber);
      void qc.invalidateQueries({ queryKey: scheduleListKey(siteId) });
      push({ kind: 'success', title: t('scheduler.create.created', { name: scheduleName }) });
      navigate(`/scheduler/${encodeURIComponent(siteId)}/${encodeURIComponent(scheduleNumber)}`);
    },
    onError: () => push({ kind: 'error', title: t('scheduler.create.failed') }),
  });

  if (siteId === '' || schedules.length === 0) return null;

  return (
    <>
      <ScheduleMenu
        schedules={schedules}
        selectedScheduleNumber={selectedScheduleNumber}
        onSelect={(scheduleNumber) => setActiveSchedule(siteId, scheduleNumber)}
        onImportFile={canEdit ? (file, format) => importMutation.mutate({ file, format }) : undefined}
        importing={importMutation.isPending}
        onCreateNew={canEdit ? () => setCreateOpen(true) : undefined}
        triggerVariant="breadcrumb"
      />
      <ChevronRightIcon size={13} />
      <CreateScheduleModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={(name) => createMutation.mutate(name)}
        creating={createMutation.isPending}
      />
    </>
  );
}
